import { NetworkStatus, gql } from '@apollo/client';
import { Query } from '@apollo/client/react/components';
import IconWarning from '@apollo/icons/default/IconWarning.svg';
import {
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
  Code,
  Group,
  Link,
  SortOrder,
  Table,
  TableContainer,
  TableSortButton,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@apollo/orbit';
import { orderBy } from 'lodash';
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';

import { clientsRouteConfig, insightsRouteConfig } from 'src/app/graph/routes';
import { FilterProvider } from 'src/components/common/filterProvider/FilterProvider';
import { Loading } from 'src/components/common/loading/Loading';
import { OperationTypeBadge } from 'src/components/common/operationTypeBadge/OperationTypeBadge';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { date } from 'src/lib/date/date';
import { format } from 'src/lib/format';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isSubscription } from 'src/lib/metrics';

import { UNIDENTIFIED_CLIENT } from '../constants';

export const CLIENT_DETAILS_QUERY = gql`
  query UI__ClientsDetailsQuery(
    $serviceId: ID!
    $timeFrom: Timestamp!
    $timeTo: Timestamp!
    $filter: ServiceQueryStatsFilter
  ) {
    service(id: $serviceId) {
      id
      stats: statsWindow(from: $timeFrom, to: $timeTo) {
        queryStats(filter: $filter) {
          metrics {
            requestsWithErrorsCount
            totalRequestCount
            registeredOperationCount
          }
          groupBy {
            querySignature
            queryName
            queryId
          }
        }
      }
    }
  }
`;

const SORT_FUNCTIONS: Record<
  string,
  (
    queryStat: GraphQLTypes.UI__ClientsDetailsQuery_service_stats_queryStats,
  ) => number | string | null
> = {
  queryName: ({ groupBy }) => groupBy.queryName,
  requestsCount: ({ metrics }) => metrics.totalRequestCount,
  errorPercentage: ({ metrics }) =>
    metrics.requestsWithErrorsCount / metrics.totalRequestCount,
};

const OperationsTable = ({
  title,
  subtitle,
  queryStats,
  clientName,
  clientVersion,
}: {
  title: string;
  subtitle: string;
  queryStats: GraphQLTypes.UI__ClientsDetailsQuery_service_stats_queryStats[];
  clientName: string | null;
  clientVersion?: string | null;
}) => {
  const location = useLocation<Record<string, unknown>>();
  const sanitizedClientName =
    clientName === UNIDENTIFIED_CLIENT || !clientName ? '' : clientName;

  const [state, setState] = useState({
    columnToSortOn: 'requestsCount',
    sortOrder: 'desc' as SortOrder,
  });

  const getSortedValue = SORT_FUNCTIONS[state.columnToSortOn];

  return (
    <Card isDivided className="m-8">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{subtitle}</CardDescription>
      </CardHeader>
      <CardBody>
        <TableContainer>
          <Table
            className="table-fixed"
            sortBy={state.columnToSortOn}
            sortOrder={state.sortOrder}
            onRequestSort={({ sortBy, sortOrder }) => {
              setState({ columnToSortOn: sortBy, sortOrder });
            }}
          >
            <colgroup>
              <col className="w-56" />
              <col />
              <col className="w-36" />
              <col className="w-36" />
            </colgroup>
            <Thead>
              <Tr>
                <Th>
                  <Group>
                    Operation name <TableSortButton attribute="queryName" />
                  </Group>
                </Th>
                {/* Included to allow the first column to have a min width */}
                <Th />
                <Th isNumeric>
                  <Group>
                    <TableSortButton
                      attribute="requestsCount"
                      defaultSortOrder="desc"
                    />
                    Requests
                  </Group>
                </Th>
                <Th isNumeric>
                  <Group>
                    <TableSortButton
                      attribute="errorPercentage"
                      defaultSortOrder="desc"
                    />
                    Errors
                  </Group>
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {orderBy(queryStats, getSortedValue, [state.sortOrder]).map(
                (queryStat) => (
                  <Tr key={Object.values(queryStat.groupBy).join(':')}>
                    <Td colSpan={2}>
                      <div className="flex items-center gap-1">
                        <Link
                          to={insightsRouteConfig.locationFrom({
                            location,
                            fromRouteConfig: clientsRouteConfig,
                            patch: {
                              clients: {
                                // '*' will include _all_ versions of a given client in the filter
                                [sanitizedClientName]: clientVersion
                                  ? [clientVersion]
                                  : '*',
                              },
                              query: queryStat.groupBy.queryId || undefined,
                              queryName:
                                queryStat.groupBy.queryName || undefined,
                            },
                          })}
                          className="block truncate"
                        >
                          {queryStat.groupBy.queryName ||
                            queryStat.groupBy.querySignature}
                        </Link>
                        <Code className="ml-1">
                          {format.shortQueryId(queryStat.groupBy.queryId || '')}
                        </Code>
                        <OperationTypeBadge
                          signature={queryStat.groupBy.querySignature}
                        />
                      </div>
                    </Td>
                    <Td isNumeric>
                      <Text as="div" variant="code" className="truncate">
                        {format.requests(queryStat.metrics.totalRequestCount)}
                      </Text>
                    </Td>
                    <Td isNumeric>
                      {isSubscription(
                        queryStat.groupBy.querySignature || '',
                      ) && (
                        <Tooltip
                          openDelay={200}
                          label="This does not currently include any field execution
              errors from subscription operations."
                        >
                          <IconWarning
                            data-testid="errors-subscriptions-tooltip"
                            className="mr-2 text-icon-warning"
                          />
                        </Tooltip>
                      )}
                      <Text as="span" variant="code" className="truncate">
                        {format.percent(
                          queryStat.metrics.requestsWithErrorsCount /
                            queryStat.metrics.totalRequestCount,
                        )}
                      </Text>
                    </Td>
                  </Tr>
                ),
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </CardBody>
    </Card>
  );
};

/**
 *
 * @param clientInput a clientName, clientVersion, or clientReferenceId to add
 *        to our query filter for stats from Druid.
 *
 * This checking between null and undefined feels pretty icky, but it also
 * seems like the fastest path to building this query filter correctly.
 *
 * We need to send an empty string in our filter here to Druid to actually fetch
 * results with "no clients/undentified clients" intentionally when we want that
 * result.
 *
 * Here, we find that `null` marks intentionality to fetch unidentified clients,
 * where as `undefined` marks a lack of specification, which means we fetch all
 * clients, not just those that are not identified.
 *
 * The addition of this function fixes a bug where the "Unidentified clients"
 * section of our clients page was previously fetching "all clients", as opposed
 * to those that were intentionally unidentified.
 *
 */
const buildClientInput = (
  clientInput: string | undefined | null,
): string | undefined => {
  return clientInput === null || clientInput === UNIDENTIFIED_CLIENT
    ? ''
    : clientInput;
};

const ClientDetails = ({
  clientName,
  clientVersion,
  graphHasRegisteredOperationsReported,
  graphId,
}: React.HTMLAttributes<HTMLDivElement> & {
  clientName: string | null;
  clientVersion?: string | null;
  graphHasRegisteredOperationsReported: boolean;
  graphId: string;
}) => {
  return (
    <div className="flex h-full flex-col">
      <FilterProvider>
        {({ timeFrom, timeTo, selectedRange, filter }) => {
          const mergedFilter = {
            ...filter,
            clientVersion: buildClientInput(clientVersion),
            clientName: buildClientInput(clientName),
          };

          return (
            <Query<
              GraphQLTypes.UI__ClientsDetailsQuery,
              GraphQLTypes.UI__ClientsDetailsQueryVariables
            >
              query={CLIENT_DETAILS_QUERY}
              variables={{
                serviceId: graphId,
                timeTo:
                  date.getDateBounds(selectedRange, {
                    from: timeFrom,
                    to: timeTo,
                  }).timeTo || '-0',
                timeFrom:
                  date.getDateBounds(selectedRange, {
                    from: timeFrom,
                    to: timeTo,
                  }).timeFrom || '-3600',
                filter: mergedFilter,
              }}
              notifyOnNetworkStatusChange={true}
            >
              {({ data, error, networkStatus }) => {
                if (error) {
                  console.error(error); // eslint-disable-line no-console
                  throw error;
                }

                if (
                  [
                    NetworkStatus.refetch,
                    NetworkStatus.loading,
                    NetworkStatus.setVariables,
                  ].includes(networkStatus)
                ) {
                  return <Loading />;
                }

                if (!data || !data.service) {
                  throw new Error('no data');
                }

                const queryStats = data.service.stats?.queryStats ?? [];
                const unregisteredOperations = queryStats.filter(
                  ({ metrics: { registeredOperationCount } }) =>
                    registeredOperationCount === 0,
                );
                return (
                  <div>
                    {graphHasRegisteredOperationsReported &&
                    unregisteredOperations?.length ? (
                      <OperationsTable
                        clientName={clientName}
                        clientVersion={clientVersion}
                        queryStats={unregisteredOperations}
                        title="Unregistered operations"
                        subtitle={`${format.number(
                          unregisteredOperations.length,
                        )} unregistered`}
                      />
                    ) : (
                      ''
                    )}
                    <OperationsTable
                      clientName={clientName}
                      clientVersion={clientVersion}
                      queryStats={queryStats ?? []}
                      title="All operations"
                      subtitle={`${format.number(
                        queryStats.length,
                      )} operations`}
                    />
                  </div>
                );
              }}
            </Query>
          );
        }}
      </FilterProvider>
    </div>
  );
};

export { ClientDetails };
